import {
	CatalogPriceSource,
	type OrderShippingMethod,
	type SuggestOrderQuoteItemPricingQuery,
} from '@codezo/graphql';
import { inFormCurrency } from '../../../pricing/catalog-suggestions';
import { applyPriceEdit } from '../../../pricing/tier-pricing';
import { isDeliveryBlank } from '../delivery-rules';
import type { DeliveryValues, PricingValues } from './types';

export type PricingSuggestion =
	SuggestOrderQuoteItemPricingQuery['suggestOrderQuoteItemPricing'][number];

/**
 * A suggestion answered by the supplier's own price request rather than the
 * catalogue. It carries a cost and no markup — the supplier states cost, the
 * admin sets the margin — plus the supplier's shipping per method.
 */
export const isVetterSuggestion = (sug: PricingSuggestion) =>
	sug.source === CatalogPriceSource.Vetter;

/**
 * A supplier's answer for one tier — the shape both a VETTER suggestion and a
 * row of the link's own `prices` satisfy, so either can fill a tier.
 */
type ShippingPrice = {
	shippingMethod: OrderShippingMethod;
	price: number;
	deliveryDaysMin?: number | null;
	deliveryDaysMax?: number | null;
};

export type VetterCost = Pick<
	PricingSuggestion,
	'baseUnitPrice' | 'currency' | 'minProductionDays' | 'maxProductionDays'
> & {
	shippingPrices: ShippingPrice[];
	/** The same cost and shipping in the form's currency, at today's rate. */
	converted?: {
		currency: string;
		baseUnitPrice: number;
		shippingPrices: ShippingPrice[];
	} | null;
	vetterEntity?: { id: string; name: string } | null;
};

const str = (n: number | null | undefined) => (n == null ? '' : String(n));

/**
 * Fills a tier from a supplier's answer, in the form's currency. Null when the
 * answer cannot be restated in it (no rate for the pair).
 *
 * Cost goes through `applyPriceEdit`, so whichever of sale price or markup the
 * admin already chose is held and the other recomputed — and an untouched tier
 * is left with no sale price, which the unpriced-tier block then insists on.
 * The margin is never defaulted.
 *
 * Shipping prefills only methods the admin has left blank: a delivery price
 * someone typed is theirs.
 */
export function applyVetterSuggestion(
	row: PricingValues,
	sug: VetterCost,
	formCurrency: string
): PricingValues | null {
	const cost = inFormCurrency(sug, formCurrency);
	if (!cost) return null;
	const prices = applyPriceEdit(
		{ cost: row.price, salePrice: row.salePrice, markup: row.markup, entryMode: row.entryMode },
		'cost',
		String(cost.baseUnitPrice)
	);
	// Shipping at the same rate as the cost, so the deliveries land in the form's
	// currency too.
	const shipping = cost.quoted ? (sug.converted?.shippingPrices ?? []) : sug.shippingPrices;

	const deliveries: DeliveryValues[] = row.deliveries.map((d) => ({ ...d }));
	for (const sp of shipping) {
		const filled: DeliveryValues = {
			shippingMethod: sp.shippingMethod,
			price: String(sp.price),
			deliveryDaysMin: str(sp.deliveryDaysMin),
			deliveryDaysMax: str(sp.deliveryDaysMax),
		};
		const i = deliveries.findIndex((d) => d.shippingMethod === sp.shippingMethod);
		if (i === -1) deliveries.push(filled);
		else if (isDeliveryBlank(deliveries[i])) deliveries[i] = filled;
	}

	return {
		...row,
		price: prices.cost,
		salePrice: prices.salePrice,
		markup: prices.markup,
		entryMode: prices.entryMode,
		minDays: sug.minProductionDays != null ? String(sug.minProductionDays) : row.minDays,
		maxDays: sug.maxProductionDays != null ? String(sug.maxProductionDays) : row.maxDays,
		// Not a catalogue break — left null so the row isn't mistaken for one.
		sourceQuantity: null,
		sourceVetterEntityId: sug.vetterEntity?.id ?? null,
		touched: true,
		autoApplied: false,
		quoted: cost.quoted,
		// What the supplier really quoted, in their own currency.
		vetterQuote: {
			vetterName: sug.vetterEntity?.name ?? 'the supplier',
			currency: sug.currency,
			shipping: Object.fromEntries(
				sug.shippingPrices.map((sp) => [sp.shippingMethod, sp.price])
			),
			prefilled: Object.fromEntries(shipping.map((sp) => [sp.shippingMethod, sp.price])),
		},
		deliveries,
	};
}
